// Demo story: ~180 pickup orders over the last 12 months, created by the outlet
// for the recycler. Every recycler-side view (collections, pending approval,
// incoming, fleet assignments, facility incoming) should derive from this one
// array so numbers reconcile across apps.

import { DEMO_MATERIAL_IDS, MATERIAL_PRICE_SAR_PER_KG } from '../catalog';
import { DEMO_OUTLET_ORG_ID, DEMO_RECYCLER_ORG_ID, ids } from '../constants';
import { DAY_MS, HOUR_MS, daysAgo, iso, round, rngFor, seasonal, type Rng } from '../prng';
import { containersAtSite, truckById } from './fleet';
import { DEMO_SITES, facilityForCity } from './places';

export type DemoPickupStatus = 'pending' | 'accepted' | 'in_transit' | 'completed' | 'cancelled';
export type DemoApprovalStatus = 'pending' | 'approved' | 'rejected';

export interface DemoPickupOrder {
  id: string;
  /** Human-facing number, e.g. 'PO-2026-0142'. */
  reference: string;
  outlet_org_id: string;
  recycler_org_id: string;
  site_id: string;
  container_id: string;
  material_id: string;
  status: DemoPickupStatus;
  created_at: string;
  scheduled_at: string;
  completed_at: string | null;
  estimated_kg: number;
  verified_kg: number | null;
  /** Recycler approval of the verified weight; set on completed orders only. */
  approval_status: DemoApprovalStatus | null;
  /** verified_kg x price when approved, 0 when rejected, null otherwise. */
  sale_amount_sar: number | null;
  facility_id: string | null;
  truck_id: string | null;
  driver_id: string | null;
  /** Container fill % when the order was raised. */
  fill_level: number;
}

/** Status quotas for 180 orders: 15 / 0 / 12 / 65 / 8 % (pending / accepted / in_transit / completed / cancelled). */
const STATUS_QUOTA: Array<[DemoPickupStatus, number]> = [
  ['pending', 27],
  ['accepted', 0],
  ['in_transit', 22],
  ['completed', 117],
  ['cancelled', 14],
];

/** How many of the most recent completed orders still wait for recycler approval. */
const PENDING_APPROVAL_COUNT = 6;

const SITE_WEIGHTS = [0.24, 0.16, 0.14, 0.22, 0.13, 0.11];

const M = DEMO_MATERIAL_IDS;
const KG_RANGE: Record<string, [number, number]> = {
  // A pickup clears the site's baled/stored stock of that stream, not just one bin.
  [M.occ]: [600, 3200],
  [M.mixed_paper]: [250, 1100],
  [M.office_paper]: [150, 600],
  [M.pet]: [150, 700],
  [M.hdpe]: [100, 450],
  [M.ldpe_film]: [200, 900],
  [M.aluminium_cans]: [60, 260],
  [M.uco]: [100, 450],
  [M.wood_pallets]: [500, 1800],
};

const RIYADH_ACTIVE_TRUCKS = [1, 2, 3, 4, 6];
const JEDDAH_ACTIVE_TRUCKS = [7, 8];
const RIYADH_TRANSIT_TRUCKS = [3, 6];
const JEDDAH_TRANSIT_TRUCKS = [7];

function weightedIndex(rng: Rng, weights: number[]): number {
  const total = weights.reduce((a, b) => a + b, 0);
  let x = rng.next() * total;
  for (let i = 0; i < weights.length; i += 1) {
    x -= weights[i];
    if (x < 0) return i;
  }
  return weights.length - 1;
}

const SEASONAL_TRIES = 8;

/**
 * Days-ago offset for a completed/cancelled order, denser in the cooler, busier
 * months. Always consumes the same number of draws, so the rest of an order
 * (site, material, weights) does not change when the calendar moves on.
 */
function seasonalDayOffset(rng: Rng, min: number, max: number): number {
  let chosen: number | null = null;
  for (let attempt = 0; attempt < SEASONAL_TRIES; attempt += 1) {
    const d = rng.int(min, max);
    const accept = rng.next() < seasonal(daysAgo(d).getUTCMonth(), 0.8, 0.2);
    if (chosen === null && (accept || attempt === SEASONAL_TRIES - 1)) chosen = d;
  }
  return chosen ?? min;
}

function atHour(day: Date, hour: number): Date {
  return new Date(day.getTime() + hour * HOUR_MS);
}

function notAfter(d: Date, limit: Date): Date {
  return d.getTime() > limit.getTime() ? limit : d;
}

/** Fresh array of demo pickup orders, oldest first (ids ascend with created_at). */
export function generatePickupOrders(): DemoPickupOrder[] {
  const rng = rngFor('pickup-orders');
  const statuses = rng.shuffle(STATUS_QUOTA.flatMap(([status, n]) => Array.from({ length: n }, () => status)));
  // Minute precision keeps timestamps tidy and repeat calls within a minute identical.
  const now = new Date(Math.floor(Date.now() / 60000) * 60000);

  type Draft = Omit<DemoPickupOrder, 'id' | 'reference'>;
  const drafts: Draft[] = statuses.map((status) => {
    const site = DEMO_SITES[weightedIndex(rng, SITE_WEIGHTS)];
    const container = rng.pick(containersAtSite(site.id));
    const material_id = container.material_id;
    const isDc = site.site_type === 'distribution_center';

    let d: number;
    if (status === 'completed') d = seasonalDayOffset(rng, 4, 364);
    else if (status === 'cancelled') d = seasonalDayOffset(rng, 2, 364);
    else if (status === 'in_transit') d = rng.int(1, 2);
    else d = rng.int(0, 13);

    const created = notAfter(atHour(daysAgo(d), rng.float(5, 15, 2)), new Date(now.getTime() - rng.int(10, 90) * 60000));
    let scheduled: Date;
    let completed: Date | null = null;
    if (status === 'completed') {
      scheduled = atHour(daysAgo(d - rng.int(1, 2)), rng.float(4, 11, 2));
      completed = new Date(scheduled.getTime() + rng.float(1, 5, 2) * HOUR_MS);
    } else if (status === 'in_transit') {
      // created is at the latest yesterday 15:00 UTC, so the clamp can never land before it.
      scheduled = notAfter(new Date(created.getTime() + rng.int(2, 8) * HOUR_MS), new Date(now.getTime() - 30 * 60000));
    } else {
      scheduled = new Date(created.getTime() + rng.int(1, 3) * DAY_MS);
    }

    const [lo, hi] = KG_RANGE[material_id] ?? [100, 600];
    const estimated_kg = Math.round((rng.int(lo, hi) * (isDc ? 1.6 : 1)) / 5) * 5;
    const verified_kg = status === 'completed' ? round(estimated_kg * rng.float(0.86, 1.08, 3), 1) : null;

    let truck_id: string | null = null;
    let driver_id: string | null = null;
    let facility_id: string | null = null;
    if (status === 'completed' || status === 'in_transit') {
      const pool =
        status === 'in_transit'
          ? site.city === 'Jeddah' ? JEDDAH_TRANSIT_TRUCKS : RIYADH_TRANSIT_TRUCKS
          : site.city === 'Jeddah' ? JEDDAH_ACTIVE_TRUCKS : RIYADH_ACTIVE_TRUCKS;
      truck_id = ids.truck(rng.pick(pool));
      driver_id = truckById(truck_id)?.driver_id ?? null;
      facility_id = facilityForCity(site.city).id;
    }

    let approval_status: DemoApprovalStatus | null = null;
    let sale_amount_sar: number | null = null;
    if (status === 'completed' && verified_kg !== null) {
      approval_status = rng.chance(0.06) ? 'rejected' : 'approved';
      sale_amount_sar = approval_status === 'approved' ? round(verified_kg * (MATERIAL_PRICE_SAR_PER_KG[material_id] ?? 0), 2) : 0;
    }

    return {
      outlet_org_id: DEMO_OUTLET_ORG_ID,
      recycler_org_id: DEMO_RECYCLER_ORG_ID,
      site_id: site.id,
      container_id: container.id,
      material_id,
      status,
      created_at: iso(created),
      scheduled_at: iso(scheduled),
      completed_at: completed ? iso(completed) : null,
      estimated_kg,
      verified_kg,
      approval_status,
      sale_amount_sar,
      facility_id,
      truck_id,
      driver_id,
      fill_level: rng.int(72, 100),
    };
  });

  // The most recent completed orders are still waiting for the recycler to approve the weight.
  drafts
    .filter((o) => o.status === 'completed')
    .sort((a, b) => (b.completed_at ?? '').localeCompare(a.completed_at ?? ''))
    .slice(0, PENDING_APPROVAL_COUNT)
    .forEach((o) => {
      o.approval_status = 'pending';
      o.sale_amount_sar = null;
    });

  drafts.sort((a, b) => a.created_at.localeCompare(b.created_at));
  return drafts.map((o, i) => ({
    id: ids.pickupOrder(i + 1),
    reference: `PO-${o.created_at.slice(0, 4)}-${String(i + 1).padStart(4, '0')}`,
    ...o,
  }));
}
